import { useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth, OFFICE_AUTO_TIMECARD_ROLES } from '@/context/AuthContext';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const CHECK_INTERVAL_MS = 30000;
const DEFAULT_TIMEOUT_MINUTES = 15;
const LAST_ACTIVITY_KEY = 'bw_last_activity';

// TEMA D: office-type roles (admin, superadmin, office, manager) are logged
// out automatically after N minutes without mouse/keyboard/touch activity —
// configurable in Settings. Field roles are never affected. The last-activity
// timestamp lives in localStorage so activity in ANY open tab keeps every
// tab's session alive (no premature logout just because one tab is idle).
export function useIdleLogout() {
  const { user, logout } = useAuth();
  const timeoutMsRef = useRef(DEFAULT_TIMEOUT_MINUTES * 60 * 1000);

  const isEligible = !!user && OFFICE_AUTO_TIMECARD_ROLES.includes(user.role);

  const markActivity = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }, []);

  useEffect(() => {
    if (!isEligible) return;
    let cancelled = false;
    api.get('/settings/company').then((res) => {
      if (cancelled) return;
      const minutes = Number(res.data?.inactivity_timeout_minutes) || DEFAULT_TIMEOUT_MINUTES;
      timeoutMsRef.current = minutes * 60 * 1000;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isEligible]);

  useEffect(() => {
    if (!isEligible) return;
    markActivity();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActivity, { passive: true }));

    const interval = setInterval(() => {
      const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (Date.now() - last > timeoutMsRef.current) {
        clearInterval(interval);
        logout();
        // Full browser navigation (NOT react-router's navigate). A client-side
        // navigate races against ProtectedRoute's own `<Navigate to="/login"/>`
        // (fired the instant `user` becomes null) and consistently loses —
        // the plain, query-less redirect wins and strips ?reason=idle before
        // LoginPage can read it. A hard redirect sidesteps that router race
        // entirely. The short delay lets the fire-and-forget auto-clock-out
        // request (inside logout()) leave the browser before the page unloads.
        setTimeout(() => {
          window.location.href = '/login?reason=idle';
        }, 250);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity));
      clearInterval(interval);
    };
  }, [isEligible, markActivity, logout]);
}
